import React, { useEffect, useState } from 'react';
import Table, { MultiSelectFilter, EditBoxColumnFilter, SelectOptionColumnFilter, DateRangeColumnFilter, CheckboxColumnFilter, includesOr, dateBetween } from './controls/Table';
import EditableTextBox from './controls/EditableTextBox';
import SplitExpenseTransactionModal from './controls/SplitExpenseTransactionModal';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';

function FinancesExpenses() {
  const [data, setData] = useState([]);
  const [splitModalTransaction, setSplitModalTransaction] = useState(null);
  const [searchParams] = useSearchParams();
  const groupBy = searchParams.get('groupby');

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      const queryString = window.location.search;
      const response = await fetch(`api/expenses/getuserexpenses${queryString}`);
      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenSplitModal = (transaction) => {
    setSplitModalTransaction(transaction);
  };

  const handleCloseSplitModal = () => {
    setSplitModalTransaction(null);
  };

  const handleSubmitSplit = async (parentId, splits) => {
    await postSplitTransaction(parentId, splits);
    handleCloseSplitModal();
    await getData();
  };

  const postSplitTransaction = async (parentId, splits) => {
    try {
      const response = await fetch('/api/expenses/splitexpensetransaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentId,
          splits,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to split transaction: ${errorText}`);
      }

      //return await response.json();
    } catch (err) {
      console.error(err);
      alert(`Error splitting transaction: ${err.message}`);
      //throw err;
    }
  };


  const handleRunRulesClick = async () => {
    try {
      const response = await fetch('api/expenses/RunNewExpenseGeneralRules');
      //const data = await response.json();
      //setData(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdate = async (id, fieldName, newValue) => {
    try {
      await updateTransactionField(id, fieldName, newValue);

      // Update the specific row in local state
      /*setData((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, [fieldName]: newValue } : row
        )
      );*/
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const updateTransactionField = async (id, fieldName, newValue) => {
    try {
      const response = await fetch(`/api/expenses/UpdateExpenseTransactionFields/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [fieldName]: newValue,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      //const result = await response.json();
      //console.log('Update successful:', result);
      //return result;
    } catch (error) {
      console.error('Update failed:', error);
      throw error;
    }
  };

  const groupColumns = React.useMemo(
    () => [
      {
        Header: 'Group',
        accessor: 'group',
      },
      {
        Header: 'Amount',
        accessor: 'totalAmount',
        Cell: ({ value }) => { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value); },
        Footer: (info) => {
          const total = info.rows.reduce((sum, row) => row.values.totalAmount + sum, 0);
          return total.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        },
      },
      {
        Header: 'Needs Review',
        accessor: 'totalNeedsReview',
        Footer: (info) => {
          const total = info.rows.reduce((sum, row) => row.values.totalNeedsReview + sum, 0);
          return total.toString();
        },
      },
    ],
    []
  );

  const expensesColumns =
    //    React.useMemo(
    //    () =>
    [
      {
        Header: 'Id',
        accessor: 'id',
        Footer: '',
      },
      {
        Header: 'Needs Review',
        accessor: 'needsReview',
        Filter: CheckboxColumnFilter,
        Cell: ({ row }) => {
          const transaction = row.original;

          const handleChange = async (e) => {
            const checked = e.target.checked;
            transaction.needsReview = checked;
            await handleUpdate(transaction.id, 'needsReview', checked);
          };

          return (
            <div>
              <Button variant="outline-secondary" onClick={() => handleOpenSplitModal(transaction)} title="Split">
                ↰↱
                {transaction.parent?.subTransactions?.length > 0 && (
                  <sub className="ms-1 text-muted" style={{ fontSize: '0.7em' }}>
                    {transaction.parent.subTransactions.length}
                  </sub>
                )}
              </Button>
              <input
                type="checkbox"
                checked={transaction.needsReview}
                onChange={handleChange}
                style={{ marginLeft: '20px', width: '20px', height: '20px' }} // Adjust size as needed
              />
            </div>
          );
        },
        Footer: info => {
          return <>Count: {info.rows.length}</>;
        }
      },
      {
        Header: 'Account',
        accessor: 'accountName',
        Filter: MultiSelectFilter,
        filter: includesOr,
        Footer: '',
      },
      {
        Header: 'Date',
        accessor: 'date',
        Filter: DateRangeColumnFilter,
        filter: dateBetween,
        Cell: ({ value }) => new Date(value).toLocaleDateString('en-US'),
        Footer: '',
      },
      {
        Header: 'Merchant',
        accessor: 'merchant',
        Filter: EditBoxColumnFilter,
        Footer: '',
        Cell: ({ row, value }) => {
          return (
            <EditableTextBox
              initialValue={value}
              onSave={(newValue) => handleUpdate(row.original.id, 'merchant', newValue)}
              minWidth='100px' />
          );
        },
      },
      {
        Header: 'Description',
        accessor: 'description',
        Filter: EditBoxColumnFilter,
        Footer: '',
        Cell: ({ row, value }) => {
          return (
            <EditableTextBox
              initialValue={value}
              onSave={(newValue) => handleUpdate(row.original.id, 'description', newValue)}
            />
          );
        },
      },
      {
        Header: 'ItemDescription',
        accessor: 'itemDescription',
        Footer: '',
      },
      {
        Header: 'Ship Date',
        accessor: 'shipDate',
        Cell: ({ value }) => new Date(value).toLocaleDateString('en-US'),
        Footer: '',
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        Cell: ({ value }) => { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value); },
        Footer: info => {
          const total = info.rows.reduce((sum, row) => row.values.amount + sum, 0);
          return <>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}</>;
        },
      },
      {
        Header: 'Category',
        accessor: 'category',
        Filter: EditBoxColumnFilter,
        //aggregate: 'count',
        //Aggregated: ({ value }) => `${value} items`,
        Footer: '',
        Cell: ({ row, value }) => {
          return (
            <EditableTextBox
              initialValue={value}
              onSave={(newValue) => handleUpdate(row.original.id, 'category', newValue)}
              minWidth='100px'
            />
          );
        },
      },
      {
        Header: 'PurchasedFor',
        accessor: 'purchasedFor',
        Filter: EditBoxColumnFilter,
        Footer: '',
        Cell: ({ row, value }) => {
          return (
            <EditableTextBox
              initialValue={value}
              onSave={(newValue) => handleUpdate(row.original.id, 'purchasedFor', newValue)}
              minWidth='100px'
            />
          );
        },
      },
      {
        Header: 'Tag',
        accessor: 'tag',
        Filter: EditBoxColumnFilter,
        Footer: '',
        Cell: ({ row, value }) => {
          return (
            <EditableTextBox
              initialValue={value}
              onSave={(newValue) => handleUpdate(row.original.id, 'tag', newValue)}
              minWidth='150px'
            />
          );
        },
      },
      {
        Header: 'Original Fields',
        accessor: 'originalField1',
        Filter: EditBoxColumnFilter,
        Cell: ({ row }) => {
          return (<span title={row.original.originalField1}>
            {row.original.originalField1 == null ? "" : row.original.originalField1.slice(0, 20)}
          </span>);
        },
        /*Cell: ({ row }) => {
          const transaction = row.original;
          return (
            <>
              {transaction.originalField1.slice(0, 20)}<br />
              {transaction.originalField2.slice(0, 20)}<br />
              {transaction.originalField3}
            </>
          );
        },*/
        Footer: '',
      },
      //  ],
      //  []
      //);
    ];

  const buttonStyle = {
    padding: "2px",
    fontSize: "20px",
    marginLeft: "10px",
    border: 'none',
    backgroundColor: 'white',
  };

  const getNestedData = (row) => {
    return row.original.transactions || [];
  };

  return (
    <div>
      <h2>Expenses <button style={buttonStyle} onClick={handleRunRulesClick}>(&#10227;)</button></h2>
      <br></br>
      {groupBy !== null && groupBy !== 'none' ?
        <Table
          columns={groupColumns}
          data={data}
          nestedColumns={expensesColumns}
          getNestedData={getNestedData}>
        </Table>
        :
        <Table
          columns={expensesColumns}
          data={data}>
        </Table>
      }
      {splitModalTransaction && (
        <SplitExpenseTransactionModal
          transaction={splitModalTransaction}
          onClose={handleCloseSplitModal}
          onSubmit={handleSubmitSplit}
        />
      )}
    </div >
  );
}

export default FinancesExpenses;